import { modelHelperModule } from './model-helper';
import * as StringUtils from '../utils/string-utils';

let uniqueIndex = 0;

const workflowTypeLabels = {
  simple_approve: 'Approve Workflow',
  simple_reject: 'Reject Workflow',
};

const teamCandidateStateLabels = {
  approved: 'Auto-Approve',
  rejected: 'Auto-Reject',
  archived: 'Auto-Archive',
};
const teamCandidateStateIcons = {
  approved: 'thumbs-up',
  rejected: 'thumbs-down',
  archived: 'archive',
};

function definePropKey(model, enKey, initial) {
  if (model[enKey] === undefined) {
    Object.defineProperty(model, enKey, {
      value: initial,
      configurable: true,
      writable: true,
    });
  }
}

const whenTypeLabels = {
  scoreMax: 'at or below',
  scoreMin: 'at least',
  timeMax: 'less than',
  timeMin: 'more than',
};

export const assessmentWorkflowPathModelModule = angular
  .module('Q.models.assessment-workflow-path', [modelHelperModule])

  /**
   * Utility class to help with assessment workflows, which are partially shared between
   * custom workflows and the default workflow on the Assessment
   */
  .factory('AssessmentWorkflowHelper', function ($injector, DS) {
    function createAction(msg, icon, tooltip) {
      return {
        toString() {
          return msg;
        },
        icon: 'icon-' + icon,
        tooltip,
      };
    }

    const percUnit = '<span class="unit">%</span>';
    const hourUnit = '<span class="unit">h</span>';
    const minUnit = '<span class="unit">m</span>';
    const ltCompare = '<span class="comparator">≤</span>';
    const gtCompare = '<span class="comparator">≥</span>';
    const isCompare = '<span class="comparator">is</span>';
    const minSymbol = (v) => (v ? ltCompare : isCompare);
    const maxSymbol = (v) => (v < 100 ? gtCompare : isCompare);
    const timeRenderer = (t) => {
      let timeString = t + minUnit;
      if (t > 60) {
        let h = Math.floor(t / 60);
        let m = t % 60;
        timeString = h + hourUnit;
        if (m) timeString += ' ' + m + minUnit;
      }
      return timeString;
    };

    return {
      /**
       * Returns an array of the actions that will be taken by the workflow or assessment
       * passed in.
       * @param data {Object} AssessmentWorkflowPath or Assessment to be scanned
       * @returns {Array<Object>}
       */
      actions(data) {
        const PT = $injector.has('PT') && $injector.get('PT');
        const participant = (PT && PT.participant) || 'candidate';
        const actions = [];

        if (data.submissionMessage) {
          actions.push(
            createAction(
              `Show custom message to ${participant}`,
              'description',
              'Message: "' +
                StringUtils.limit(data.submissionMessage, 50, ' […]') +
                '"',
            ),
          );
        }

        if (data.retryAction) {
          actions.push(
            createAction(
              `${data.retryAction === 'retake' ? 'Retake' : 'Reopen'} Assessment`,
              'undo',
            ),
          );
        }

        if (data.nextAssessmentId) {
          let nextAssessment = DS.get('Assessment', data.nextAssessmentId);
          actions.push(
            createAction(
              `Send ${participant} to ${nextAssessment ? '"' + nextAssessment.title + '"' : 'Next Assessment'}`,
              'substitute',
            ),
          );
        }

        if (data.redirectUrl) {
          actions.push(
            createAction(
              `Redirect ${participant} to URL`,
              'link',
              data.redirectUrl,
            ),
          );
        }

        let tcState =
          teamCandidateStateLabels[data.assignmentTeamCandidateState];
        if (tcState) {
          actions.push(
            createAction(
              `${tcState} ${participant}`,
              teamCandidateStateIcons[data.assignmentTeamCandidateState],
            ),
          );
        }

        let teamMemberIds = data.assignmentTeamMemberIds;
        let teamMemberCount = teamMemberIds && teamMemberIds.length;
        if (teamMemberCount) {
          let plural = teamMemberCount > 1 ? 's' : '';
          let fn = DS.get('TeamMember', teamMemberIds[0]);
          let msg = plural
            ? `${teamMemberCount} team members`
            : (fn && fn.name) || 'team member';
          actions.push(
            createAction(
              `Assign to ${msg}`,
              'user' + plural,
              plural &&
                teamMemberIds
                  .map((tmid) => DS.get('TeamMember', tmid))
                  .filter(angular.identity)
                  .map((tm) => tm.nameOrEmail)
                  .join(', '),
            ),
          );
        }

        let labels = data.assignmentLabels || [];
        if (labels.length > 0) {
          let plural = labels.length === 1 ? '' : 's';
          actions.push(
            createAction(
              `Add to label${plural} "${labels.join('", "')}"`,
              'label' + plural,
            ),
          );
        }

        if (data.webhookUrl) {
          actions.push(
            createAction('Send Webhook Event', 'webhooks', data.webhookUrl),
          );
        }
        return actions;
      },

      /**
       * Returns a formatted strings representing the conditions for the given workflow
       * @param data
       * @returns {string}
       */
      conditions(data) {
        let cond = [];

        if (data.scoreMin && data.scoreMax) {
          cond.push(
            `${data.scoreMin}${percUnit} ${ltCompare} Score ${ltCompare} ${data.scoreMax}${percUnit}`,
          );
        } else if (angular.isNumber(data.scoreMax)) {
          cond.push(
            `Score ${minSymbol(data.scoreMax)} ${data.scoreMax}${percUnit}`,
          );
        } else if (angular.isNumber(data.scoreMin)) {
          cond.push(
            `Score ${maxSymbol(data.scoreMin)} ${data.scoreMin}${percUnit}`,
          );
        }

        let timeLabel =
          data.timeType === 'total' ? 'Total Time' : 'Active Time';
        if (data.timeMin && data.timeMax) {
          cond.push(
            `${timeRenderer(data.timeMin)} ${ltCompare} ${timeLabel} ${ltCompare} ${timeRenderer(data.timeMax)}`,
          );
        } else if (data.timeMin) {
          cond.push(`${timeLabel} ${gtCompare} ${timeRenderer(data.timeMin)}`);
        } else if (data.timeMax) {
          cond.push(`${timeLabel} ${ltCompare} ${timeRenderer(data.timeMax)}`);
        }

        return cond.join(` <span class="op">${data.conditionsOp}</span> `);
      },

      /**
       * Creates a utility method for the workflow action toggles. This method acts as a getter/setter,
       * taking care of setting the default value or removing the value for the various string-format actions
       * @param key {String} Action key
       * @param initialValue {String?} The value to set when the action is enabled.
       * @returns {Function} Returns a getter/setter function for the key
       */
      actionEnabledString(key, initialValue = '') {
        const enKey = `$${key}Enabled`;
        return function actionEnabled(val) {
          let currentAction = !!this[key];
          definePropKey(this, enKey, currentAction);
          if (val === undefined) return currentAction || this[enKey];
          this[enKey] = val;
          if (val === true) {
            this[key] = this[key] || initialValue;
          } else {
            this[key] = null;
          }
        };
      },

      /**
       * Creates a utility method for the workflow action toggles. This method acts as a getter/setter,
       * taking care of removing the values for the various array-format actions
       * @param key {String} Action key
       * @returns {Function} Returns a getter/setter function for the key
       */
      actionEnabledArray(key) {
        const enKey = `$${key}Enabled`;
        return function actionEnabled(val) {
          let currentAction = !!(this[key] || []).length;
          definePropKey(this, enKey, currentAction);
          if (val === undefined) return currentAction || this[enKey];
          this[enKey] = val;
          if (val === false) {
            this[key] = [];
          }
        };
      },
    };
  })

  .factory(
    'AssessmentWorkflowPath',
    function (ModelHelper, AssessmentWorkflowHelper) {
      function initData(data) {
        if (!data.$uniqueIndex) {
          // ensure we have a unique ID for repeaters
          let id;
          if (data.assessmentId && data.workflowType) {
            // these are unique to the assessment, so we use a static value
            id = data.assessmentId + '-' + data.workflowType;
          } else if (data.id) {
            // custom workflows, it's OK if they get refreshed
            id = data.id;
          } else {
            // Otherwise, use a generated ID for client-side ordering
            id = ++uniqueIndex;
          }
          data.$uniqueIndex = id;
        }
        if (!data.name && data.workflowType) {
          // set the default name
          data.name = workflowTypeLabels[data.workflowType];
        }
      }

      const AssessmentWorkflowPath = ModelHelper.defineResource({
        name: 'AssessmentWorkflowPath',
        endpoint: 'assessment_workflow_paths',

        beforeCreateInstance(resource, data) {
          initData(data);
        },

        beforeInject(resource, data) {
          if (!Array.isArray(data)) data = [data];
          data.forEach((d) => {
            initData(d);
            // denormalize the actions and conditions
            d.$actions = AssessmentWorkflowHelper.actions(d);
            d.$conditions = AssessmentWorkflowHelper.conditions(d);
          });
        },

        staticMethods: {
          scoreWhenTypes: [
            { value: 'scoreMin', text: whenTypeLabels['scoreMin'] },
            { value: 'scoreMax', text: whenTypeLabels['scoreMax'] },
          ],
          timeWhenTypes: [
            { value: 'timeMax', text: whenTypeLabels['timeMax'] },
            { value: 'timeMin', text: whenTypeLabels['timeMin'] },
          ],
        },

        methods: {
          isValid() {
            // filters out invalid workflows to prevent errors when saving
            // these are workflows without a condition
            // returns undefined for invalid so it can be used in an ngModel
            return (
              ['timeMin', 'timeMax', 'scoreMin', 'scoreMax'].some((k) =>
                angular.isNumber(this[k]),
              ) || undefined
            );
          },
          label() {
            return (
              this.name ||
              workflowTypeLabels[this.workflowType] ||
              'Custom Workflow'
            );
          },
          isSimpleApprove() {
            return this.workflowType === 'simple_approve';
          },
          isSimpleReject() {
            return this.workflowType === 'simple_reject';
          },

          // Action getter/setters
          assignmentTeamCandidateStateEnabled:
            AssessmentWorkflowHelper.actionEnabledString(
              'assignmentTeamCandidateState',
              'approved',
            ),
          submissionMessageEnabled:
            AssessmentWorkflowHelper.actionEnabledString('submissionMessage'),
          nextAssessmentIdEnabled: AssessmentWorkflowHelper.actionEnabledString(
            'nextAssessmentId',
            null,
          ),
          assignmentTeamMemberIdsEnabled:
            AssessmentWorkflowHelper.actionEnabledArray(
              'assignmentTeamMemberIds',
            ),
          assignmentLabelsEnabled:
            AssessmentWorkflowHelper.actionEnabledArray('assignmentLabels'),
          redirectUrlEnabled:
            AssessmentWorkflowHelper.actionEnabledString('redirectUrl'),
          webhookUrlEnabled:
            AssessmentWorkflowHelper.actionEnabledString('webhookUrl'),
          retryActionEnabled: AssessmentWorkflowHelper.actionEnabledString(
            'retryAction',
            'reopen',
          ),

          // Used to show or hide extended actions & conditions on simple workflows
          advancedEnabled(val) {
            definePropKey(this, '$advancedShowing', !this.workflowType);
            if (val === undefined) return this.$advancedShowing;
            this.$advancedShowing = val || !this.workflowType;
          },

          scoreWhenType(newVal) {
            // defaults to scoreMin if nothing is set
            definePropKey(
              this,
              '$scoreWhenType',
              this.scoreMax ? 'scoreMax' : 'scoreMin',
            );
            if (newVal === undefined) return this.$scoreWhenType;
            if (this.$scoreWhenType !== newVal) {
              this[newVal] = this[this.$scoreWhenType];
              this[this.$scoreWhenType] = null;
              this.$scoreWhenType = newVal;
            }
          },
          scoreWhenTypeLabel(scoreWhenType) {
            return whenTypeLabels[scoreWhenType || this.scoreWhenType()];
          },

          timeWhenType(newVal) {
            // defaults to timeMax if nothing is set
            definePropKey(
              this,
              '$timeWhenType',
              this.timeMin ? 'timeMin' : 'timeMax',
            );
            if (newVal === undefined) return this.$timeWhenType;
            if (this.$timeWhenType !== newVal) {
              this[newVal] = this[this.$timeWhenType];
              this[this.$timeWhenType] = null;
              this.$timeWhenType = newVal;
            }
          },
          timeWhenTypeLabel(timeWhenType) {
            return whenTypeLabels[timeWhenType || this.timeWhenType()];
          },
        },

        relations: {
          belongsTo: {
            Assessment: {
              localField: 'assessment',
              localKey: 'assessmentId',
            },
          },
          hasMany: {
            TeamMember: {
              localField: 'assignmentTeamMembers',
              localKeys: 'assignmentTeamMemberIds',
            },
          },
          hasOne: {
            Assessment: {
              localField: 'nextAssessment',
              localKey: 'nextAssessmentId',
            },
          },
        },

        meta: {
          track: {
            create: true,
            update: true,
          },
        },
      });

      return AssessmentWorkflowPath;
    },
  );
